/**
 * 销售账号管理 API client（运营主管「招销售」，sales_account:manage）。
 *
 * 对应 admin-service /sales-accounts：只能碰 role='sales' 的账号。
 * 与通用 /admins 的区别：创建时角色恒为 sales（请求体只有 unionId + nickname），
 * 响应里不会带 unionId；企微只回「是否已绑」，不回成员账号本身。
 * 所有写动作由 client.ts 自动注入 Idempotency-Key。
 */
import { apiClient } from './client';

export type SalesAccountStatus = 'active' | 'disabled';

export interface SalesAccountDto {
  id: string;
  nickname: string;
  status: SalesAccountStatus;
  /** 是否已绑企业微信成员（不返回账号本身） */
  wecomBound: boolean;
  createdByNickname: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  /** 名下客户数；业务库读不到时为 null */
  customerCount: number | null;
}

export interface SalesAccountCreateQuota {
  /** 单人窗口内最多创建数 */
  limit: number;
  windowHours: number;
  /** 当前操作者窗口内已创建数 */
  used: number;
}

export interface ListSalesAccountsResponse {
  items: SalesAccountDto[];
  total: number;
  createQuota: SalesAccountCreateQuota;
}

export interface CreateSalesAccountInput {
  unionId: string;
  nickname: string;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/** GET /sales-accounts */
export async function listSalesAccounts(): Promise<ListSalesAccountsResponse> {
  const resp = await apiClient.get<ServiceResponse<ListSalesAccountsResponse>>('/sales-accounts');
  return resp.data.data;
}

/** POST /sales-accounts —— 创建销售账号（立即生效）。请求体只含 unionId + nickname。 */
export async function createSalesAccount(input: CreateSalesAccountInput): Promise<void> {
  await apiClient.post<ServiceResponse<unknown>>('/sales-accounts', {
    unionId: input.unionId,
    nickname: input.nickname,
  });
}

/** PATCH /sales-accounts/:id/wecom —— 绑定 / 解绑（null）企业微信成员账号。 */
export async function setSalesAccountWecom(id: string, wecomUserId: string | null): Promise<void> {
  await apiClient.patch<ServiceResponse<unknown>>(`/sales-accounts/${encodeURIComponent(id)}/wecom`, {
    wecomUserId,
  });
}

/** POST /sales-accounts/:id/disable —— 停用（联动撤销会话 + 拉黑 token）。 */
export async function disableSalesAccount(id: string, reason?: string): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/sales-accounts/${encodeURIComponent(id)}/disable`,
    reason ? { reason } : {},
  );
}

/** POST /sales-accounts/:id/enable —— 启用。 */
export async function enableSalesAccount(id: string): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(`/sales-accounts/${encodeURIComponent(id)}/enable`, {});
}
